import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import format from 'date-fns/format';

import { scrollToBottom } from '../../../../../../utils/messages';
import { MessageTypes, Link, CustomCompMessage, GlobalState } from '../../../../../../store/types';
import { setBadgeCount, markAllMessagesRead } from '../../../../../../store/actions';
import { MESSAGE_SENDER } from '../../../../../../constants';
import { INTRO_BANNER_SECTIONS, SHOW_INTRO_BANNER } from '../../../../../../../../config';

import Loader from './components/Loader';
import './styles.scss';
import React from 'react';

type Props = {
  showTimeStamp: boolean,
  profileAvatar?: string;
  profileClientAvatar?: string;
  /** Called with the bot message id and the rating when the user clicks thumbs up/down. */
  onMessageFeedback?: (messageId: string, rating: 'liked' | 'disliked') => void;
}

const LAST_FEEDBACK_MESSAGE_KEY = 'askAiLastFeedbackMessageId';
const FEEDBACK_VISIBLE_DURATION_MS = 5000;
const FEEDBACK_FADE_DURATION_MS = 2000;

const getMessageIdentifier = (message: MessageTypes | Link | CustomCompMessage): string | null => {
  if (message.customId) return String(message.customId);
  const timestamp = (message as any).timestamp;
  if (!timestamp) return null;
  if (timestamp instanceof Date) {
    return timestamp.getTime().toString();
  }
  const parsed = new Date(timestamp).getTime();
  return Number.isNaN(parsed) ? null : parsed.toString();
};

function Messages({ profileAvatar, profileClientAvatar, showTimeStamp, onMessageFeedback }: Props) {
  const dispatch = useDispatch();
  const { messages, typing, showChat, badgeCount } = useSelector((state: GlobalState) => ({
    messages: state.messages.messages,
    badgeCount: state.messages.badgeCount,
    typing: state.behavior.messageLoader,
    showChat: state.behavior.showChat
  }));

  const prevMessagesLengthRef = useRef(messages.length);

  const messageRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (messages.length !== prevMessagesLengthRef.current) {
      // @ts-ignore
      scrollToBottom(messageRef.current);
    }
    prevMessagesLengthRef.current = messages.length;

    if (showChat && badgeCount) dispatch(markAllMessagesRead());
    else dispatch(setBadgeCount(messages.filter((message) => message.unread).length));
  }, [messages, badgeCount, showChat]);

  useEffect(() => {
    if (showChat) {
      // @ts-ignore
      scrollToBottom(messageRef.current);
    }
  }, [showChat]);

  // Ensure typing indicator is visible by scrolling to bottom when typing changes
  useEffect(() => {
    // @ts-ignore
    scrollToBottom(messageRef.current);
  }, [typing]);

  const assistantMeta = useMemo(() => {
    const indices: number[] = [];
    messages.forEach((msg, idx) => {
      if (msg.sender === MESSAGE_SENDER.RESPONSE) {
        indices.push(idx);
      }
    });

    const lastIndex = indices.length ? indices[indices.length - 1] : -1;
    const firstIndex = indices.length ? indices[0] : -1;
    const lastMessage = lastIndex >= 0 ? messages[lastIndex] : null;

    let lastTimestamp: number | null = null;
    if (lastMessage?.timestamp) {
      const timestampMs =
        lastMessage.timestamp instanceof Date
          ? lastMessage.timestamp.getTime()
          : new Date(lastMessage.timestamp).getTime();
      if (!Number.isNaN(timestampMs)) {
        lastTimestamp = timestampMs;
      }
    }

    return {
      indices,
      lastIndex,
      firstIndex,
      lastMessage,
      lastTimestamp
    };
  }, [messages]);

  const lastAssistantIndex = assistantMeta.lastIndex;
  const lastAssistantMessage = assistantMeta.lastMessage;
  const lastAssistantMessageId = useMemo(() => {
    if (!lastAssistantMessage) return null;
    return getMessageIdentifier(lastAssistantMessage);
  }, [lastAssistantMessage]);

  const [feedbackState, setFeedbackState] = useState<'idle' | 'pending' | 'submitted'>('idle');
  const [feedbackRating, setFeedbackRating] = useState<'liked' | 'disliked' | null>(null);
  const [feedbackThanksPhase, setFeedbackThanksPhase] = useState<'hidden' | 'visible' | 'fading'>('hidden');
  const [persistedFeedbackMessageId, setPersistedFeedbackMessageId] = useState<string | null>(() =>
    typeof window !== 'undefined' ? window.localStorage.getItem(LAST_FEEDBACK_MESSAGE_KEY) : null
  );
  const thanksTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thanksFadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (thanksTimeoutRef.current) {
      clearTimeout(thanksTimeoutRef.current);
      thanksTimeoutRef.current = null;
    }
    if (thanksFadeTimeoutRef.current) {
      clearTimeout(thanksFadeTimeoutRef.current);
      thanksFadeTimeoutRef.current = null;
    }

    if (lastAssistantMessageId && persistedFeedbackMessageId === lastAssistantMessageId) {
      setFeedbackState('submitted');
      setFeedbackRating(null);
      setFeedbackThanksPhase('hidden');
      return;
    }

    setFeedbackState('idle');
    setFeedbackRating(null);
    setFeedbackThanksPhase('hidden');
  }, [lastAssistantMessageId, persistedFeedbackMessageId]);

  useEffect(() => () => {
    if (thanksTimeoutRef.current) {
      clearTimeout(thanksTimeoutRef.current);
      thanksTimeoutRef.current = null;
    }
    if (thanksFadeTimeoutRef.current) {
      clearTimeout(thanksFadeTimeoutRef.current);
      thanksFadeTimeoutRef.current = null;
    }
  }, []);

  const isLastAssistantRecent = useMemo(() => {
    if (!assistantMeta.lastTimestamp) return false;
    const THREE_MINUTES_MS = 3 * 60 * 1000;
    return Date.now() - assistantMeta.lastTimestamp <= THREE_MINUTES_MS;
  }, [assistantMeta.lastTimestamp]);

  const recordFeedback = async (rating: 'liked' | 'disliked') => {
    if (!lastAssistantMessage || feedbackState === 'pending') return;

    const generatedMessageId = lastAssistantMessageId || `message-${Date.now()}`;

    try {
      setFeedbackState('pending');
      setFeedbackRating(rating);

      // Feedback is recorded in the local conversation history by the widget orchestrator.
      await Promise.resolve(onMessageFeedback?.(generatedMessageId, rating));
      setFeedbackState('submitted');
      setFeedbackThanksPhase('visible');
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LAST_FEEDBACK_MESSAGE_KEY, generatedMessageId);
      }
      setPersistedFeedbackMessageId(generatedMessageId);
      if (thanksTimeoutRef.current) {
        clearTimeout(thanksTimeoutRef.current);
      }
      if (thanksFadeTimeoutRef.current) {
        clearTimeout(thanksFadeTimeoutRef.current);
      }
      thanksTimeoutRef.current = setTimeout(() => {
        setFeedbackThanksPhase('fading');
        thanksTimeoutRef.current = null;
        thanksFadeTimeoutRef.current = setTimeout(() => {
          setFeedbackThanksPhase('hidden');
          thanksFadeTimeoutRef.current = null;
        }, FEEDBACK_FADE_DURATION_MS);
      }, FEEDBACK_VISIBLE_DURATION_MS);
    } catch (error) {
      console.error('Failed to submit message feedback', error);
      setFeedbackState('idle');
      setFeedbackRating(null);
    }
  };
    
  const getComponentToRender = (message: MessageTypes | Link | CustomCompMessage) => {
    const ComponentToRender = message.component;
    if (message.type === 'component') {
      return <ComponentToRender {...message.props} />;
    }
    return <ComponentToRender message={message} showTimeStamp={showTimeStamp} />;
  };

  // TODO: Fix this function or change to move the avatar to last message from response
  // const shouldRenderAvatar = (message: Message, index: number) => {
  //   const previousMessage = messages[index - 1];
  //   if (message.showAvatar && previousMessage.showAvatar) {
  //     dispatch(hideAvatar(index));
  //   }
  // }

  const isClient = (sender) => sender === MESSAGE_SENDER.CLIENT;
  
  // Check if the last message is from AI (RESPONSE) to turn off typing indicator
  const lastMessageIsFromAI = messages.length > 0 && messages[messages.length - 1].sender === MESSAGE_SENDER.RESPONSE;
  const shouldShowTyping = (!lastMessageIsFromAI) || messages.length === 0;
  
  const hasClientMessage = useMemo(
    () => messages.some((message) => message.sender === MESSAGE_SENDER.CLIENT),
    [messages]
  );

  const showIntroBanner = SHOW_INTRO_BANNER && !hasClientMessage;

  return (
    <div id="messages" className="rcw-messages-container" ref={messageRef}>
      {showIntroBanner && (
        <div className="rcw-beta-thread-banner">
          {INTRO_BANNER_SECTIONS.map(({ id, text }) => (
            <p key={id} className={`rcw-beta-thread-banner__${id}`}>
              {text}
            </p>
          ))}
        </div>
      )}
      {messages?.map((message, index) => {
        const clientMessage = isClient(message.sender);
        const component = getComponentToRender(message);
        const assistantCount = assistantMeta.indices.length;
        const isLastAssistantMessage = index === lastAssistantIndex;
        const messageIdentifier = getMessageIdentifier(message);
        const isPersistedFeedback = isLastAssistantMessage && !!messageIdentifier && messageIdentifier === persistedFeedbackMessageId;
        const showFeedback =
          !clientMessage &&
          isLastAssistantMessage &&
          isLastAssistantRecent &&
          assistantCount > 1 &&
          index !== assistantMeta.firstIndex &&
          feedbackState !== 'submitted' &&
          !isPersistedFeedback;

        return (
          <div
            className={`rcw-message${clientMessage ? ' rcw-message-client' : ''}`}
            key={`${index}-${format(message.timestamp, 'hh:mm')}`}
          >
            <div className="rcw-message-body">
              {component}
              {showFeedback && (
                <div className="rcw-message-feedback" role="group" aria-label="Evie response feedback">
                  <button
                    type="button"
                    className={`rcw-feedback-button rcw-feedback-like${feedbackRating === 'liked' ? ' active' : ''}`}
                    onClick={() => recordFeedback('liked')}
                    disabled={feedbackState === 'pending' || feedbackRating === 'liked'}
                    aria-pressed={feedbackRating === 'liked'}
                    aria-label="Mark response as helpful"
                    title="Helpful"
                  >
                    <span aria-hidden="true">👍</span>
                  </button>
                  <button
                    type="button"
                    className={`rcw-feedback-button rcw-feedback-dislike${feedbackRating === 'disliked' ? ' active' : ''}`}
                    onClick={() => recordFeedback('disliked')}
                    disabled={feedbackState === 'pending' || feedbackRating === 'disliked'}
                    aria-pressed={feedbackRating === 'disliked'}
                    aria-label="Mark response as not helpful"
                    title="Unhelpful"
                  >
                    <span aria-hidden="true">👎</span>
                  </button>
                </div>
              )}
              {isLastAssistantMessage && feedbackState === 'submitted' && !clientMessage && (
                <div className={`rcw-feedback-thanks rcw-feedback-thanks--${feedbackThanksPhase}`}>
                  Thanks for your feedback!
                </div>
              )}
            </div>
          </div>
        );
      })}
      <Loader typing={shouldShowTyping} />
    </div>
  );
}

export default Messages;
